import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    ReactNode,
} from 'react';
import {
    ThemeMode,
    ThemeVariables,
    themeVariables,
} from './theme-variables';
export type { ThemeMode } from './theme-variables';

interface ThemeContextValue {
    mode: ThemeMode;
    isDark: boolean;
    isLight: boolean;
    setTheme: (mode: ThemeMode) => void;
    toggleTheme: () => void;
}

const noop = () => undefined;

const ThemeContext = createContext<ThemeContextValue>({
    mode: 'dark',
    isDark: true,
    isLight: false,
    setTheme: noop,
    toggleTheme: noop,
});

const applyThemeVariables = (variables: ThemeVariables) => {
    if (typeof document === 'undefined') {
        return;
    }

    const root = document.documentElement;
    Object.entries(variables).forEach(([name, value]) => {
        root.style.setProperty(name, value);
    });
};

interface ThemeContextProviderProps {
    children: ReactNode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({ children }) => {

    // get theme from localStorage at startup
    const getInitialTheme = (): ThemeMode => {
        if (typeof window !== 'undefined') {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'light' || savedTheme === 'dark') return savedTheme;
        }
        return 'dark'; // default value
    };

    const [mode, setMode] = useState<ThemeMode>(getInitialTheme);

    useEffect(() => {
        applyThemeVariables(themeVariables[mode]);
        // Save the theme in localStorage
        localStorage.setItem('theme', mode);
    }, [mode]);

    const value = useMemo<ThemeContextValue>(
        () => ({
            mode,
            isDark: mode === 'dark',
            isLight: mode === 'light',
            setTheme: setMode,
            toggleTheme: () => setMode(prev => (prev === 'dark' ? 'light' : 'dark')),
        }),
        [mode],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
    const context = useContext(ThemeContext);

    if (!context) {
        throw new Error('useThemeContext must be used within ThemeContextProvider');
    }

    return context;
};
